import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { canUseAgenticSourcing } from '../../lib/sourcing'
import {
  sourcingGroupedBySupplier,
  sourcingCount,
  addToSourcing,
  isInSourcing,
  removeFromSourcing,
  subscribeSourcing,
  sourcingSeeds,
  clearSourcing,
  type SourcingGroup,
} from '../../lib/sourcingList'
import { searchApi, wantListApi, sourcingApi, type BuyerWantView, type BuyerSettlementView } from '../../api/endpoints'
import { stoneKey } from '../../lib/stoneKey'
import { formatPrice, formatPerCarat, formatCarat, displayStockId } from '../../lib/format'

/**
 * SourcingStatusBoard — the body of Profile → "My sourcing"
 * (docs/ASKA-PLUS-AGENTIC-SOURCING.md §2.2, build slice 1). The buyer's
 * read-mostly monitoring surface: their current sourcing LIST (what they've
 * queued to source) plus a STUBBED status board (outreach sent · waiting ·
 * confirmed lanes) that the live thread data fills in slice 3.
 *
 * Rendered by BOTH shells (desktop AccountSourcing + mobile MySourcingScreen),
 * so the two can't drift — the twin of how /sourcing wraps SourcingPanel.
 *
 * ⚠️ Self-gates on `canUseAgenticSourcing()`: off-flag it shows the same pitch
 * as WantListPanel (the tile that routes here is hidden off-flag anyway, but a
 * deep link must never dead-end). Armed with an empty list ⇒ an empty state.
 */
/** One sourcing JOB (a submitted want-list) + its wants' live status — the buyer
 *  monitors a job, expands it to see each stone's lanes/quotes (§13.2). */
type JobView = { id: string; createdAt: string; status: string; wants: BuyerWantView[] }

export function SourcingStatusBoard() {
  const navigate = useNavigate()
  const [armed, setArmed] = useState<boolean>(() => canUseAgenticSourcing())
  const [count, setCount] = useState<number>(() => sourcingCount())
  const [jobs, setJobs] = useState<JobView[] | null>(null)
  const [expanded, setExpanded] = useState<string | null>(null)
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    const onFlag = () => setArmed(canUseAgenticSourcing())
    const onList = () => setCount(sourcingCount())
    window.addEventListener('aska:sourcing-changed', onFlag)
    const unsub = subscribeSourcing(onList)
    onList()
    return () => {
      window.removeEventListener('aska:sourcing-changed', onFlag)
      unsub()
    }
  }, [])

  // Load the buyer's sourcing JOBS (the want-lists they submitted, newest first),
  // each with its wants' live status (ranked quotes + lanes). Best-effort: an
  // un-armed server ({configured:false}) or a fetch error leaves `jobs` empty and
  // the board shows the example lanes instead — never a dead end.
  useEffect(() => {
    if (!armed) return
    let cancelled = false
    ;(async () => {
      try {
        const lists = await wantListApi.list()
        if (!lists.configured || !lists.rfqConfigured || !lists.wantLists?.length) { if (!cancelled) setJobs([]); return }
        const recent = [...lists.wantLists].sort((a, b) => b.createdAt.localeCompare(a.createdAt)).slice(0, 10)
        const built = await Promise.all(recent.map(async (list) => {
          const views = await Promise.all(
            list.wantIds.map(async (id) => {
              try { return (await wantListApi.quotes(id)).want ?? null } catch { return null }
            }),
          )
          return { id: list.id, createdAt: list.createdAt, status: list.status, wants: views.filter((v): v is BuyerWantView => !!v) }
        }))
        const withWants = built.filter((j) => j.wants.length > 0)
        if (!cancelled) {
          setJobs(withWants)
          // Auto-expand the newest job (or keep the buyer's current choice).
          setExpanded((prev) => prev ?? withWants[0]?.id ?? null)
        }
      } catch {
        if (!cancelled) setJobs([])
      }
    })()
    return () => { cancelled = true }
  }, [armed, reloadKey])

  if (!armed) {
    // Deep-link fallback — mirror WantListPanel's inert pitch, never a dead end.
    return (
      <div className="rounded-card border border-border bg-paper p-6 space-y-3">
        <h2 className="font-serif text-xl font-bold text-ink">Let your agent do the sourcing.</h2>
        <p className="text-sm leading-relaxed text-soft">
          Build a list of the stones you’re after — your Aska+ sourcing agent reaches out to the
          right suppliers, collects real quotes, and brings you a ranked shortlist.
        </p>
        <span className="inline-flex items-center rounded-full bg-navy/5 px-3 py-1 text-xs font-semibold text-soft">Coming soon · Aska+</span>
      </div>
    )
  }

  const groups = sourcingGroupedBySupplier()

  return (
    <div className="space-y-8">
      {/* Active requests. When the buyer has LIVE requests (submitted want-lists),
          render each with its real per-supplier lanes + ranked quotes. Otherwise
          show the example lanes so the shape of "My sourcing" is legible. */}
      {jobs && jobs.length > 0 ? (
        <section>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-soft">Your sourcing jobs</h2>
          <div className="mt-3 space-y-3">
            {jobs.map((job) => (
              <JobCard
                key={job.id}
                job={job}
                open={expanded === job.id}
                onToggle={() => setExpanded((cur) => (cur === job.id ? null : job.id))}
                onRefresh={() => setReloadKey((k) => k + 1)}
              />
            ))}
          </div>
        </section>
      ) : (
        <section>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-soft">Active requests</h2>
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            {LANES.map((lane) => (
              <div key={lane.key} className="rounded-2xl border border-border bg-paper p-4">
                <div className="flex items-center gap-2">
                  <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full ${lane.tint}`}>
                    {lane.icon}
                  </span>
                  <p className="text-[13px] font-semibold text-ink">{lane.title}</p>
                </div>
                <p className="mt-2 text-[12px] leading-snug text-soft">{lane.desc}</p>
                <p className="mt-3 text-[11px] font-semibold uppercase tracking-wide text-soft/70">No requests yet</p>
              </div>
            ))}
          </div>
          <p className="mt-3 text-[12px] text-soft">
            Once you start sourcing, each request appears here and moves left to right as suppliers reply.
          </p>
        </section>
      )}

      {/* Add by stock ID — the direct path when the buyer already knows the
          exact stone (not just the ＋ on a browse/chat card). Resolves the ID
          server-side and drops it straight onto the list. */}
      <AddByStockId />

      {/* The current LIST — what's queued to source (the tray's contents,
          shown here so Profile is the single place to review + prune it). */}
      <section>
        <div className="flex items-baseline justify-between gap-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-soft">Your sourcing list</h2>
          {count > 0 && <span className="text-[12px] tabular-nums text-soft">{count} {count === 1 ? 'stone' : 'stones'}</span>}
        </div>
        {count === 0 ? (
          <p className="mt-3 text-sm text-soft">
            No stones on your list yet. Tap <span className="font-semibold text-ink">＋ Add to sourcing</span> on
            any diamond in Browse or chat, then open the tray to start sourcing.
          </p>
        ) : (
          <div className="mt-3 space-y-5">
            {groups.map((g) => (
              <GroupBlock key={g.supplierName ?? '__pending'} group={g} />
            ))}
            {/* Start from here — same flow as the tray's "Start sourcing", so a buyer
                who built the list on this page (or via add-by-ID) doesn't have to
                bounce to the floating tray. */}
            <StartSourcingButton onStarted={() => { setCount(sourcingCount()); setExpanded(null); setReloadKey((k) => k + 1); navigate('/account/profile') }} />
          </div>
        )}
      </section>
    </div>
  )
}

/** One sourcing JOB — a collapsible card grouping every stone the buyer started
 *  sourcing together (§13.2). Collapsed: a one-line summary; expanded: each stone's
 *  LiveRequest (lanes + ranked quotes + settlement). The buyer's window into the
 *  job — they can drill in per stone. */
function JobCard({ job, open, onToggle, onRefresh }: { job: JobView; open: boolean; onToggle: () => void; onRefresh: () => void }) {
  const stones = job.wants.length
  const quoted = job.wants.filter((w) => (w.quotes?.length ?? 0) > 0).length
  const terminal = stones > 0 && job.wants.every((w) => ['cancelled', 'expired', 'purchased', 'partial'].includes(w.status))
  const dateLabel = (() => {
    const d = new Date(job.createdAt)
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  })()
  const summary = terminal ? 'No longer active'
    : quoted > 0 ? `${quoted} of ${stones} quoted`
    : 'Reaching suppliers…'
  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-paper">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        data-testid="sourcing-job-toggle"
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
      >
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 text-[13px] font-semibold text-ink">
            <span className="inline-flex items-center rounded-full bg-navy/5 px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-wide text-soft">Job</span>
            Sourcing{dateLabel ? ` · ${dateLabel}` : ''}
          </p>
          <p className="mt-0.5 text-[11.5px] text-soft">{stones} {stones === 1 ? 'stone' : 'stones'} · {summary}</p>
        </div>
        <svg
          width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
          strokeLinecap="round" strokeLinejoin="round" aria-hidden
          className={`shrink-0 text-soft transition-transform ${open ? 'rotate-180' : ''}`}
        ><path d="m6 9 6 6 6-6" /></svg>
      </button>
      {open && (
        <div className="space-y-3 border-t border-border p-4">
          {job.wants.map((w) => <LiveRequest key={w.id} want={w} onRefresh={onRefresh} />)}
        </div>
      )}
    </div>
  )
}

/** Start-sourcing action — the twin of SourcingTray.onStart (create want-list from
 *  the local seeds → submit → clear the local list). Graceful "coming soon" when the
 *  server isn't armed; the enrolled/allowlist copy on a real failure. */
function StartSourcingButton({ onStarted }: { onStarted: () => void }) {
  const [state, setState] = useState<'idle' | 'starting' | 'soon' | 'error'>('idle')
  const [errText, setErrText] = useState<string | null>(null)

  async function onStart() {
    if (state === 'starting') return
    setState('starting'); setErrText(null)
    try {
      const created = await wantListApi.create(sourcingSeeds())
      if (!created.configured || !created.rfqConfigured || !created.wantList) {
        setState('soon'); return
      }
      await wantListApi.submit(created.wantList.id)
      clearSourcing()
      onStarted()
    } catch (e) {
      const msg = e instanceof Error ? e.message : ''
      setErrText(/not.?enrolled|allowlist|limited testing/i.test(msg)
        ? 'Agentic sourcing is in limited testing — your account isn’t enrolled yet.'
        : 'Couldn’t start sourcing just now — please try again in a moment.')
      setState('error')
    }
  }

  if (state === 'soon') {
    return (
      <div className="rounded-2xl border border-border bg-cream/50 p-4 text-center">
        <p className="text-[13px] font-semibold text-ink">Sourcing is coming soon</p>
        <p className="mt-1 text-[12px] leading-relaxed text-soft">Your list is saved — when your agent switches on we’ll reach the right suppliers and bring back a ranked shortlist.</p>
        <span className="mt-2 inline-flex items-center rounded-full bg-navy/5 px-3 py-1 text-[11px] font-semibold text-soft">Beta · Aska+</span>
      </div>
    )
  }
  return (
    <div>
      {errText && <p className="mb-2 text-[12px] text-danger" role="status" data-testid="sourcing-board-start-error">{errText}</p>}
      <button
        type="button"
        onClick={onStart}
        disabled={state === 'starting'}
        data-testid="sourcing-board-start"
        className="btn-primary flex h-11 w-full items-center justify-center gap-2 rounded-full text-sm font-bold disabled:opacity-60"
      >
        {state === 'starting' ? 'Starting…' : 'Start sourcing'}
      </button>
      <p className="mt-2 text-center text-[11.5px] leading-snug text-soft">
        We’ll WhatsApp these suppliers for quotes — you never see or manage them.
      </p>
    </div>
  )
}

/** Paste a stock ID → resolve it server-side (`searchApi.detail`) → add to the
 *  sourcing list. The direct-entry twin of the ＋ button on cards. */
function AddByStockId() {
  const [id, setId] = useState('')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    const q = id.trim()
    if (!q || busy) return
    setBusy(true)
    setMsg(null)
    try {
      const stone = await searchApi.detail(q)
      if (isInSourcing(stoneKey(stone))) {
        setMsg({ ok: true, text: `#${displayStockId(stone)} is already on your list.` })
      } else {
        addToSourcing(stone)
        setMsg({ ok: true, text: `Added #${displayStockId(stone)} to your sourcing list.` })
      }
      setId('')
    } catch {
      setMsg({ ok: false, text: `No stone found for “${q}”. Double-check the stock ID.` })
    } finally {
      setBusy(false)
    }
  }

  return (
    <section>
      <h2 className="text-sm font-semibold uppercase tracking-wide text-soft">Add by stock ID</h2>
      <p className="mt-1 text-[12px] text-soft">Know the exact stone? Paste its stock ID and we’ll add it to your list.</p>
      <form onSubmit={submit} className="mt-3 flex gap-2">
        <input
          value={id}
          onChange={(e) => { setId(e.target.value); setMsg(null) }}
          placeholder="e.g. DX5QS00845"
          spellCheck={false}
          autoCapitalize="characters"
          autoCorrect="off"
          aria-label="Stock ID"
          data-testid="sourcing-add-by-id-input"
          className="min-w-0 flex-1 rounded-xl border border-border bg-paper px-3 py-2 text-sm text-ink placeholder:text-soft/60 focus:border-gold focus:outline-none"
        />
        <button
          type="submit"
          disabled={busy || !id.trim()}
          data-testid="sourcing-add-by-id-submit"
          className="btn-primary shrink-0 rounded-xl px-4 py-2 text-sm font-semibold disabled:opacity-50"
        >
          {busy ? 'Adding…' : 'Add'}
        </button>
      </form>
      {msg && (
        <p className={`mt-2 text-[12px] ${msg.ok ? 'text-success' : 'text-danger'}`} role="status">{msg.text}</p>
      )}
    </section>
  )
}

function GroupBlock({ group }: { group: SourcingGroup }) {
  return (
    <div>
      <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-soft">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M3 21h18M5 21V8l7-4 7 4v13M10 21v-5h4v5" /></svg>
        {group.supplierName ?? 'Supplier pending'}
      </p>
      <div className="space-y-1.5">
        {group.items.map((item) => {
          const priceLocked = item.pricePerCarat == null || item.priceUSD == null
          return (
            <div key={item.key} className="flex items-center gap-3 rounded-xl border border-border bg-cream/50 px-3 py-2">
              <div className="min-w-0 flex-1">
                <p className="truncate text-[12.5px] font-semibold text-ink">#{displayStockId(item)}</p>
                <p className="truncate text-[11px] text-soft">
                  {item.shape} · {formatCarat(item.carat)}
                  {item.color ? ` · ${item.color}` : ''}{item.clarity ? ` ${item.clarity}` : ''}
                </p>
              </div>
              <div className="shrink-0 text-right">
                {priceLocked ? (
                  <p className="text-[11px] font-semibold text-gold">Verifying</p>
                ) : (
                  <>
                    <p className="text-[12.5px] font-bold text-ink tabular-nums">{formatPerCarat(item.pricePerCarat)}</p>
                    <p className="text-[10px] text-soft tabular-nums">{formatPrice(item.priceUSD)} total</p>
                  </>
                )}
              </div>
              <button
                type="button"
                onClick={() => removeFromSourcing(item.key)}
                aria-label={`Remove ${item.stockId} from your sourcing list`}
                title="Remove"
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-soft hover:bg-paper hover:text-ink"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /></svg>
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}

/** One LIVE sourcing request — its buyer-safe status (per-supplier lanes) + the
 *  ranked quote shortlist. NEVER shows a supplier name (the server projection
 *  drops it); a lane is an anonymous "one supplier is here" chip. Once a quote is
 *  accepted, the SETTLEMENT panel (docs/…§13) takes over: stage, payment
 *  instructions, "I've paid", and per-stone shipping/tracking. */
function LiveRequest({ want, onRefresh }: { want: BuyerWantView; onRefresh: () => void }) {
  const [cancelling, setCancelling] = useState(false)
  const [confirmCancel, setConfirmCancel] = useState(false)
  const [cancelErr, setCancelErr] = useState<string | null>(null)
  const laneCounts = (want.lanes ?? []).reduce<Record<string, number>>((acc, l) => {
    acc[l.lane] = (acc[l.lane] ?? 0) + 1
    return acc
  }, {})
  const quotes = want.quotes ?? []
  const title = want.specificStockId
    ? `#${want.specificStockId}`
    : describeSpec(want.spec)

  async function doCancel() {
    if (cancelling) return
    setCancelling(true); setCancelErr(null)
    try {
      const r = await sourcingApi.cancel(want.id)
      if (r.ok || r.status === 'cancelled') onRefresh()
      else setCancelErr('Couldn’t cancel just now — try again.')
    } catch {
      setCancelErr('Couldn’t cancel just now — try again.')
    } finally {
      setCancelling(false); setConfirmCancel(false)
    }
  }

  return (
    <div className="rounded-2xl border border-border bg-paper p-4">
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-[13px] font-semibold text-ink">{title}</p>
        <span className="text-[11px] font-semibold uppercase tracking-wide text-soft">{requestStatusLabel(want)}</span>
      </div>

      {/* SETTLEMENT (accepted) takes precedence over the quote shortlist. */}
      {want.settlement ? (
        <SettlementPanel wantId={want.id} settlement={want.settlement} onRefresh={onRefresh} />
      ) : (
        <>
          {/* Per-supplier lanes — anonymized chips (count per lane), never a name. */}
          <div className="mt-2 flex flex-wrap gap-1.5">
            {LANE_ORDER.map((k) =>
              laneCounts[k] ? (
                <span key={k} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${LANE_TINT[k]}`}>
                  {LANE_LABEL[k]} · {laneCounts[k]}
                </span>
              ) : null,
            )}
            {(want.lanes?.length ?? 0) === 0 && (
              <span className="text-[11.5px] text-soft">
                {['cancelled', 'expired', 'purchased', 'partial'].includes(want.status)
                  ? 'No longer active — agent stopped.'
                  : 'Reaching suppliers…'}
              </span>
            )}
          </div>
          {/* Ranked quotes — best first, price only, never a supplier. */}
          {quotes.length > 0 && (
            <div className="mt-3 space-y-1.5">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-soft">Quotes ({quotes.length})</p>
              {quotes.map((q, i) => (
                <div key={i} className="flex items-center justify-between gap-3 rounded-xl border border-border bg-cream/50 px-3 py-2">
                  <span className="text-[12px] text-soft">
                    Quote {i + 1}{q.available ? '' : ' · unavailable'}
                    {q.aboveListed && <span className="ml-1 text-[10px] font-semibold text-gold">· above listed</span>}
                  </span>
                  <span className="text-right">
                    {q.quotedPerCarat != null && <span className="text-[12.5px] font-bold text-ink tabular-nums">{formatPerCarat(q.quotedPerCarat)}</span>}
                    {q.quotedBase != null && <span className="ml-2 text-[10px] text-soft tabular-nums">{formatPrice(q.quotedBase)} total</span>}
                    {q.shippingUsd != null && <span className="ml-2 block text-[10px] text-soft tabular-nums">+{formatPrice(q.shippingUsd)} shipping</span>}
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* CANCEL — stops the agent for a still-active request. Hidden once the want
          is terminal or its settlement is past the point of no return (shipped/
          delivered) or fulfilling (the server rejects those too). */}
      {isCancellable(want) && (
        <div className="mt-3 border-t border-border pt-3">
          {cancelErr && <p className="mb-1.5 text-[11px] text-danger" role="status">{cancelErr}</p>}
          {confirmCancel ? (
            <div className="flex items-center justify-between gap-2">
              <span className="text-[12px] text-soft">Cancel this request? This stops the agent.</span>
              <span className="flex shrink-0 gap-2">
                <button type="button" onClick={() => setConfirmCancel(false)} className="rounded-full px-3 py-1 text-[12px] font-semibold text-soft hover:text-ink">Keep</button>
                <button type="button" onClick={doCancel} disabled={cancelling} data-testid="sourcing-cancel-confirm" className="rounded-full bg-danger/10 px-3 py-1 text-[12px] font-semibold text-danger disabled:opacity-60">{cancelling ? 'Cancelling…' : 'Yes, cancel'}</button>
              </span>
            </div>
          ) : (
            <button type="button" onClick={() => setConfirmCancel(true)} data-testid="sourcing-cancel" className="text-[12px] font-semibold text-soft hover:text-danger">
              Cancel sourcing
            </button>
          )}
        </div>
      )}
    </div>
  )
}

/** Whether a live request can still be cancelled (§ cancel-guard). Terminal wants,
 *  those being fulfilled (server rejects confirming/purchased/partial), and
 *  settlements past shipping are not cancellable — matching the backend guard. */
function isCancellable(w: BuyerWantView): boolean {
  if (['cancelled', 'expired', 'purchased', 'partial', 'confirming'].includes(w.status)) return false
  if (w.settlement && ['shipped', 'delivered', 'cancelled'].includes(w.settlement.stage)) return false
  return true
}

/**
 * The OFF-PLATFORM settlement panel (docs/…§13). Shows the settlement stage, the
 * supplier's (verbatim) payment instructions with an "I've paid" attestation
 * button, and per-stone shipping (carrier + tracking) once the supplier ships.
 * NOTHING here charges — money moves buyer→supplier directly; this records the
 * buyer's attestation and displays the supplier's confirmation.
 */
function SettlementPanel({ wantId, settlement, onRefresh }: { wantId: string; settlement: BuyerSettlementView; onRefresh: () => void }) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function markPaid() {
    if (busy) return
    setBusy(true); setErr(null)
    try {
      const r = await wantListApi.markPaid(wantId)
      if (r.ok) onRefresh()
      else setErr(r.error ?? 'Could not record that just now — try again.')
    } catch {
      setErr('Could not record that just now — try again.')
    } finally {
      setBusy(false)
    }
  }

  const shipped = settlement.shipments.filter((s) => s.shipped)
  return (
    <div className="mt-3 space-y-3">
      {/* Stage timeline — the settlement's progress, buyer-safe. */}
      <div className="flex flex-wrap items-center gap-1.5">
        {SETTLEMENT_STEPS.map((step) => {
          const status = settlementStepStatus(settlement.stage, step.key)
          return (
            <span
              key={step.key}
              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                status === 'done' ? 'bg-success/15 text-success'
                  : status === 'active' ? 'bg-gold/15 text-gold'
                  : 'bg-navy/5 text-soft'
              }`}
            >
              {step.label}
            </span>
          )
        })}
      </div>

      {settlement.stage === 'dispute' && (
        <div className="rounded-xl border border-danger/30 bg-danger/5 px-3 py-2 text-[12px] text-danger">
          On hold — {settlement.disputeReason ?? 'the Aska team is looking into it'}. We’ll be in touch.
        </div>
      )}
      {settlement.stage === 'cancelled' && (
        <div className="rounded-xl border border-border bg-cream/50 px-3 py-2 text-[12px] text-soft">
          This sourcing didn’t complete — no money changed hands.
        </div>
      )}

      {/* Supplier payment instructions (verbatim, never invented) + "I've paid". */}
      {settlement.paymentInstructions && (
        <div className="rounded-xl border border-border bg-cream/50 px-3 py-2">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-soft">How to pay the supplier</p>
          <p className="mt-1 whitespace-pre-wrap text-[12.5px] leading-relaxed text-ink">{settlement.paymentInstructions}</p>
        </div>
      )}
      {settlement.awaitingBuyerPayment && (
        <div className="space-y-1.5">
          <button
            type="button"
            onClick={markPaid}
            disabled={busy}
            data-testid="sourcing-mark-paid"
            className="btn-primary w-full rounded-xl px-4 py-2 text-sm font-semibold disabled:opacity-50"
          >
            {busy ? 'Recording…' : 'I’ve paid the supplier'}
          </button>
          <p className="text-[11px] text-soft">
            Pay the supplier directly, then tap this — we’ll confirm they received it and get you tracking.
          </p>
          {err && <p className="text-[11px] text-danger" role="status">{err}</p>}
        </div>
      )}
      {settlement.buyerPaidAt && settlement.stage === 'payment_claimed' && (
        <p className="text-[12px] text-soft">Payment recorded — confirming receipt with the supplier…</p>
      )}

      {/* Shipping — per stone, carrier + tracking, once the supplier ships. */}
      {shipped.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-soft">Shipping</p>
          {shipped.map((s, i) => (
            <div key={i} className="flex items-center justify-between gap-3 rounded-xl border border-border bg-cream/50 px-3 py-2">
              <span className="text-[12.5px] font-semibold text-ink">#{s.stockId}</span>
              <span className="text-right text-[11px] text-soft">
                {s.carrier ? `${s.carrier} · ` : ''}{s.trackingRef ? <span className="font-semibold text-ink">{s.trackingRef}</span> : 'shipped'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/** The settlement stages the buyer sees, in order (dispute/cancelled render as
 *  their own banner, not a step). */
const SETTLEMENT_STEPS: { key: string; label: string }[] = [
  { key: 'accepted', label: 'Accepted' },
  { key: 'supplier_confirmed', label: 'Confirmed' },
  { key: 'payment_instructions', label: 'Pay' },
  { key: 'payment_claimed', label: 'Paid' },
  { key: 'shipped', label: 'Shipped' },
  { key: 'delivered', label: 'Delivered' },
]
const SETTLEMENT_RANK: Record<string, number> = {
  accepted: 0, supplier_confirmed: 1, payment_instructions: 2, payment_claimed: 3, shipped: 4, delivered: 5,
  dispute: -1, cancelled: -1,
}
function settlementStepStatus(stage: string, stepKey: string): 'done' | 'active' | 'pending' {
  const cur = SETTLEMENT_RANK[stage] ?? 0
  const at = SETTLEMENT_RANK[stepKey] ?? 0
  if (cur < 0) return at === 0 ? 'active' : 'pending' // disputed/cancelled — freeze at the start
  if (at < cur) return 'done'
  if (at === cur) return 'active'
  return 'pending'
}

const LANE_ORDER = ['outreach', 'waiting', 'reminded', 'quoted', 'declined'] as const
const LANE_LABEL: Record<string, string> = {
  outreach: 'Outreach sent', waiting: 'Waiting', reminded: 'Reminded', quoted: 'Quoted', declined: 'Declined',
}
const LANE_TINT: Record<string, string> = {
  outreach: 'bg-sapphire/10 text-sapphire', waiting: 'bg-gold/15 text-gold',
  reminded: 'bg-gold/15 text-gold', quoted: 'bg-success/15 text-success', declined: 'bg-navy/5 text-soft',
}

/** A short human status label for a live request, from its status + rfq +
 *  settlement (the settlement stage outranks the quote count once accepted). */
function requestStatusLabel(w: BuyerWantView): string {
  if (w.settlement) {
    switch (w.settlement.stage) {
      case 'delivered': return 'Delivered'
      case 'shipped': return 'Shipped'
      case 'payment_claimed': return 'Paid'
      case 'payment_instructions': return 'Pay supplier'
      case 'supplier_confirmed': return 'Confirmed'
      case 'dispute': return 'On hold'
      case 'cancelled': return 'Cancelled'
      case 'accepted':
      default: return 'Accepted'
    }
  }
  if (w.status === 'purchased' || w.status === 'partial') return 'Accepted'
  if (w.status === 'cancelled') return 'Cancelled'
  if (w.status === 'expired') return 'Expired'
  const quoted = (w.quotes?.length ?? 0) > 0
  if (quoted) return `${w.quotes.length} quote${w.quotes.length === 1 ? '' : 's'}`
  if (w.rfq?.status === 'contacting' || w.rfq?.status === 'awaiting_quotes') return 'Sourcing'
  return 'Sourcing'
}

/** A compact spec label ("1.5ct D VS1 round") for a spec want, from the projected
 *  filter spec. Display only. */
function describeSpec(spec: Record<string, unknown>): string {
  const parts: string[] = []
  const cmin = spec.caratMin as number | undefined
  const cmax = spec.caratMax as number | undefined
  if (cmin && cmin === cmax) parts.push(`${cmin}ct`)
  else if (cmin) parts.push(`${cmin}ct+`)
  const arr = (v: unknown): string[] => (Array.isArray(v) ? v.map(String) : [])
  const color = arr(spec.color); if (color.length) parts.push(color.join('/'))
  const clarity = arr(spec.clarity); if (clarity.length) parts.push(clarity.join('/'))
  const shape = arr(spec.shape); if (shape.length) parts.push(shape.join('/').toLowerCase())
  return parts.join(' ') || 'Your sourcing request'
}

/** The three example lanes a sourcing request moves through. */
const LANES: { key: string; title: string; desc: string; tint: string; icon: React.ReactNode }[] = [
  {
    key: 'outreach',
    title: 'Outreach sent',
    desc: 'Your agent has messaged the right suppliers for a quote.',
    tint: 'bg-sapphire/10 text-sapphire',
    icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m22 2-7 20-4-9-9-4Z" /><path d="M22 2 11 13" /></svg>,
  },
  {
    key: 'waiting',
    title: 'Waiting',
    desc: 'Suppliers are replying — quotes are being collected and ranked.',
    tint: 'bg-gold/15 text-gold',
    icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 8v4l3 2" /><circle cx="12" cy="12" r="9" /></svg>,
  },
  {
    key: 'confirmed',
    title: 'Quotes in',
    desc: 'A ranked shortlist is ready — pick the one you want.',
    tint: 'bg-success/15 text-success',
    icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>,
  },
]
